import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { authService } from '../services/AuthService';
import { useConfig } from './ConfigProvider';
import { useToast } from '../components/shared/ToastContainer';
import { WeatherLoader } from '../components/shared/WeatherLoader';

interface AuthContextValue {
  isAuthenticated: boolean;
  isInitializing: boolean;
  error: string | null;
  retry: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { config } = useConfig();
  const { showError } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initializeAuth = async () => {
    if (!config) {
      return;
    }

    setIsInitializing(true);
    setError(null);

    try {
      await authService.initialize();
      setIsAuthenticated(true);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      setIsAuthenticated(false);
      console.error('Authentication error:', err);
      
      // Show error toast with retry option
      showError(
        'Authentication failed. Please check your credentials and try again.',
        'Retry',
        retry
      );
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    initializeAuth();
  }, [config]);

  const retry = async () => {
    await initializeAuth();
  };

  if (isInitializing) {
    return (
      <div className="auth-loading">
        <WeatherLoader message="Authenticating..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-error">
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <button onClick={retry} className="retry-button">
          Retry Authentication
        </button>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, isInitializing, error, retry }}>
      {children}
    </AuthContext.Provider>
  );
};
